import { type ReactNode, useCallback, useEffect, useMemo, useReducer } from "react";
import { CheckCircle2, Info, Loader2, X } from "lucide-react";
import {
  createToast,
  dismissToast,
  toastsReducer,
  updateToast,
  type ToastInput,
  type ToastRecord,
  type ToastTone
} from "../toastStore";
import { ToastContext, type ToastContextValue } from "../toastContext";

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(toastsReducer, []);

  const showToast = useCallback((input: ToastInput) => {
    const id = input.id ?? crypto.randomUUID();
    dispatch(createToast({ ...input, id }));
    return id;
  }, []);

  const updateExistingToast = useCallback((id: string, patch: ToastInput) => {
    dispatch(updateToast(id, patch));
  }, []);

  const dismissExistingToast = useCallback((id: string) => {
    dispatch(dismissToast(id));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    showToast,
    updateToast: updateExistingToast,
    dismissToast: dismissExistingToast
  }), [dismissExistingToast, showToast, updateExistingToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissExistingToast} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastRecord[]; onDismiss: (id: string) => void }) {
  return (
    <div className="toast-viewport" aria-live="polite" aria-atomic="true">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (toast.durationMs === null) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [onDismiss, toast.durationMs, toast.id]);

  const Icon = iconForTone(toast.tone);

  return (
    <div className="toast-card" data-tone={toast.tone}>
      <Icon aria-hidden="true" className={`h-4 w-4 shrink-0 ${toast.tone === "loading" ? "animate-spin" : ""}`} />
      <div className="min-w-0 flex-1 text-sm font-medium text-white">{toast.title}</div>
      <button className="toast-close" type="button" aria-label="Dismiss notification" onClick={() => onDismiss(toast.id)}>
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}

function iconForTone(tone: ToastTone) {
  if (tone === "success") return CheckCircle2;
  if (tone === "loading") return Loader2;
  return Info;
}
